import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import dayjs from 'dayjs'
import { Gantt, Willow } from '@svar-ui/react-gantt'
import '@svar-ui/react-gantt/all.css'
import {
    buildMainTaskUpdateBodyFromTask,
    updateTaskSupport,
} from '../taskRefactored/tasks.service'
import { useDispatch, useSelector } from 'react-redux'
import { projectsState } from './projects.slice'
import { useParams } from 'react-router-dom'
import AssigneeSelectorButton from './components/AssigneeSelectorButton'
import { Spin } from 'antd'
import {
    mainTaskList,
    mainTaskUpdate,
    taskListSupport,
} from '../taskRefactored/tasks.slice'
import styles from './ProjectRoadMap.module.css'

const findTaskInLanes = (lanes = [], taskId) => {
    if (!taskId) return null
    for (const lane of lanes) {
        for (const task of lane?.list || []) {
            if (task?._id === taskId) return task
            for (const subTask of task?.subTasks || []) {
                if (subTask?._id === taskId) return subTask
            }
        }
    }
    return null
}
/**
 * Convert API data to SVAR Gantt tasks with optional tree.
 */
const mapApiToGanttTree = (data = []) => {
    const tasks = []
    const links = []
    const visitedIds = new Set()

    const toGanttTask = (task, laneId, parentId = null) => {
        const start = task?.start ? dayjs(task?.start) : dayjs()
        const end = task?.end ? dayjs(task?.end) : start.add(1, 'day')
        const duration = Math.max(1, end.diff(start, 'day') + 1)

        return {
            id: task?._id,
            text: task?.title?.trim() || 'Untitled Task',
            start: start.toDate(),
            end: end.toDate(),
            type: 'task',
            duration,
            assignee: task?.assigneeId || null,
            status: task?.status || laneId,
            ...(parentId ? { parent: parentId } : {}),
        }
    }

    data.forEach((lane) => {
        const laneTasks = lane.list || []

        laneTasks.forEach((task) => {
            if (!task?._id || visitedIds.has(task?._id)) return

            // Skip nested child rows from root level if backend also returns them in lane.list.
            if (
                Array.isArray(task?.parentTask) &&
                task?.parentTask?.length > 0
            ) {
                return
            }

            const parentTask = toGanttTask(task, lane._id)
            tasks.push(parentTask)
            visitedIds.add(task?._id)

            if (Array.isArray(task?.subTasks) && task?.subTasks.length > 0) {
                task?.subTasks.forEach((subTask) => {
                    if (!subTask?._id || visitedIds.has(subTask?._id)) return
                    const childTask = toGanttTask(subTask, lane._id, task?._id)
                    tasks.push(childTask)
                    visitedIds.add(subTask?._id)
                })
            }
        })
    })

    return { tasks, links }
}

const STATUS_COLORS = {
    Done: { bg: '#DCFCE7', color: '#166534' },
    'In Progress': { bg: '#DBEAFE', color: '#1E40AF' },
    Pending: { bg: '#FEF3C7', color: '#92400E' },
}

const ProjectRoadMap = ({
    apiData,
    onTaskClick,
    projectId: projectIdProp,
    taskListKind = 'support',
}) => {
    const dispatch = useDispatch()
    const isMainList = taskListKind === 'main'

    const apiRef = useRef(null)

    const { id: routeId } = useParams()
    const projectId = projectIdProp ?? routeId

    const { tasks, links } = useMemo(
        () => mapApiToGanttTree(apiData),
        [apiData]
    )

    const { projectStatusList } = useSelector(projectsState)

    const statusOptions = useMemo(() => {
        if (projectStatusList?.length) return projectStatusList
        return (apiData || []).map((lane) => lane?._id).filter(Boolean)
    }, [projectStatusList, apiData])

    const refreshTaskList = useCallback(() => {
        if (!projectId) return
        if (isMainList) {
            dispatch(
                mainTaskList({
                    projectId,
                    skip: 0,
                    limit: 10,
                    sort: { field: 'created', order: 'desc' },
                })
            )
            return
        }
        dispatch(
            taskListSupport({
                skip: 0,
                limit: 10,
                filter: { projectId },
                listType: 'status',
            })
        )
    }, [dispatch, projectId, isMainList])

    const [updatingAssigneeId, setUpdatingAssigneeId] = useState(null)
    const [updatingStatusId, setUpdatingStatusId] = useState(null)

    const [startDate, endDate] = useMemo(() => {
        if (!tasks.length) return [new Date(), new Date()]

        const starts = tasks.map((t) => t.start.getTime())
        const ends = tasks.map((t) => t.end.getTime())

        const minStart = new Date(Math.min(...starts))
        const maxEnd = new Date(Math.max(...ends))
        maxEnd.setMonth(maxEnd.getMonth() + 1) // padding

        return [minStart, maxEnd]
    }, [tasks])

    //     if (!row.assignee) return "-";

    //     const { name, profilePicture } = row.assignee;
    //     const firstLetter = name?.charAt(0).toUpperCase();

    //     return (
    //         <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    //             {profilePicture ? (
    //                 <img
    //                     src={profilePicture}
    //                     alt={name}
    //                     style={{
    //                         width: 24,
    //                         height: 24,
    //                         borderRadius: "50%",
    //                         objectFit: "cover"
    //                     }}
    //                 />
    //             ) : (
    //                 <div
    //                     style={{
    //                         width: 24,
    //                         height: 24,
    //                         borderRadius: "50%",
    //                         backgroundColor: "#1677ff",
    //                         color: "#fff",
    //                         display: "flex",
    //                         alignItems: "center",
    //                         justifyContent: "center",
    //                         fontSize: 12,
    //                         fontWeight: 600
    //                     }}
    //                 >
    //                     {firstLetter}
    //                 </div>
    //             )}

    //             <span>{name}</span>
    //         </div>
    //     );
    // };

    // const StatusCell = ({ row }) => {
    //     const colors = {
    //         Done: { bg: "#DCFCE7", color: "#166534" },
    //         "In Progress": { bg: "#DBEAFE", color: "#1E40AF" },
    //         Pending: { bg: "#FEF3C7", color: "#92400E" }
    //     };

    //     const style = colors[row.status] || {
    //         bg: "#E5E7EB",
    //         color: "#374151"
    //     };

    //     return (
    //         <span
    //             style={{
    //                 padding: "4px 10px",
    //                 borderRadius: 12,
    //                 fontSize: 12,
    //                 fontWeight: 500,
    //                 background: style.bg,
    //                 color: style.color
    //             }}
    //         >
    //             {row.status}
    //         </span>
    //     );
    // };
    const getAssigneeColor = (name) => {
        if (!name) return '#e2e8f0'
        const colors = [
            '#85BBF9',
            '#6366f1',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#06b6d4',
        ]
        const index = name.charCodeAt(0) % colors.length
        return colors[index]
    }

    const getInitials = (name) => {
        if (!name) return 'U'
        const parts = name.split(' ')
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase()
        }
        return name.substring(0, 2).toUpperCase()
    }

    const AssignedCell = ({ row }) => {
        const isLoading = updatingAssigneeId === row.id

        const handleClick = (e) => {
            e.stopPropagation()
            e.preventDefault()
        }

        return (
            <div onClick={handleClick} className={styles.assigneeCell}>
                {isLoading ? (
                    <Spin size="small" />
                ) : (
                    <AssigneeSelectorButton
                        selectedAssignee={row.assignee}
                        onSelect={async (assigneeId) => {
                            try {
                                setUpdatingAssigneeId(row.id)

                                // update gantt UI
                                apiRef.current?.exec('update-task', {
                                    id: row.id,
                                    task: {
                                        type: row.type || 'task',
                                        start: row.start,
                                        end: row.end,
                                        duration: row.duration,
                                        text: row.text,
                                        status: row.status,
                                        assignee: assigneeId,
                                    },
                                })

                                // update backend
                                if (isMainList) {
                                    const sourceTask = findTaskInLanes(
                                        apiData,
                                        row.id
                                    )
                                    if (sourceTask) {
                                        const body =
                                            buildMainTaskUpdateBodyFromTask(
                                                sourceTask,
                                                projectId,
                                                {
                                                    assigneeId:
                                                        assigneeId?._id ??
                                                        assigneeId,
                                                }
                                            )
                                        await dispatch(
                                            mainTaskUpdate({
                                                id: row.id,
                                                body,
                                            })
                                        )
                                        refreshTaskList()
                                    }
                                } else {
                                    const res = await updateTaskSupport({
                                        _id: row.id,
                                        updateTask: {
                                            assigneeId: assigneeId._id,
                                            projectId,
                                        },
                                    })

                                    if (res.data.success) {
                                        refreshTaskList()
                                    }
                                }
                            } catch (err) {
                                console.error('Assignee update failed', err)
                            } finally {
                                setUpdatingAssigneeId(null)
                            }
                        }}
                        showName
                        projectId={projectId}
                        taskListKind={taskListKind}
                        getAssigneeColor={getAssigneeColor}
                        getInitials={getInitials}
                        parentComponent="gantt"
                    />
                )}
            </div>
        )
    }

    const TaskCell = ({ row }) => {
        return (
            <span
                className={styles.taskCellText}
                onClick={() => onTaskClick?.(row.id)}
            >
                {row.text}
            </span>
        )
    }

    const StatusCell = ({ row }) => {
        const [value, setValue] = useState(row.status)

        useEffect(() => {
            setValue(row.status)
        }, [row.status])

        const handleChange = async (newStatus) => {
            setValue(newStatus)
            setUpdatingStatusId(row.id)

            // update gantt UI
            // apiRef.current?.exec("update-task", {
            //     id: row.id,
            //     status: newStatus,
            // });

            apiRef.current?.exec('update-task', {
                id: row.id,
                task: {
                    type: row.type || 'task',
                    start: row.start,
                    end: row.end,
                    duration: row.duration,
                    text: row.text,
                    status: newStatus,
                    assignee: row.assignee,
                },
            })

            // update backend
            try {
                if (isMainList) {
                    const sourceTask = findTaskInLanes(apiData, row.id)
                    if (sourceTask) {
                        const body = buildMainTaskUpdateBodyFromTask(
                            sourceTask,
                            projectId,
                            { status: newStatus }
                        )
                        await dispatch(mainTaskUpdate({ id: row.id, body }))
                        refreshTaskList()
                    }
                } else {
                    const res = await updateTaskSupport({
                        _id: row.id,
                        updateTask: { status: newStatus, projectId: projectId },
                    })
                    if (res.data.success) {
                        refreshTaskList()
                    }
                }
            } catch (err) {
                console.error('Status update failed', err)
            } finally {
                setUpdatingStatusId(null)
            }
        }

        const style = STATUS_COLORS[value] || {
            bg: '#E5E7EB',
            color: '#374151',
        }

        return (
            <div
                onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                }}
                className={styles.statusCell}
            >
                {updatingStatusId === row.id ? (
                    <Spin size="small" />
                ) : (
                    <select
                        value={value}
                        onChange={(e) => handleChange(e.target.value)}
                        className={styles.statusSelect}
                        style={{
                            background: style.bg,
                            color: style.color,
                        }}
                    >
                        {statusOptions?.map((status) => (
                            <option key={status} value={status}>
                                {status}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        )
    }

    const columns = [
        {
            id: 'text',
            header: 'Task',
            width: 280,
            cell: TaskCell,
        },
        {
            id: 'status',
            header: 'Status',
            width: 130,
            cell: StatusCell,
        },
        {
            id: 'assignee',
            header: 'Assigned',
            width: 200,
            cell: AssignedCell,
            sort: false,
        },
        {
            id: 'duration',
            header: 'Duration',
            width: 120,
            template: (v) => `${v} day${v > 1 ? 's' : ''}`,
        },
        {
            id: 'start',
            header: 'Start Date',
            width: 140,
            template: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
        },
        {
            id: 'end',
            header: 'End Date',
            width: 140,
            template: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
        },
    ]

    const updateTaskOnDreg = useCallback(
        async (id, start, end) => {
            try {
                if (isMainList) {
                    const sourceTask = findTaskInLanes(apiData, id)
                    if (!sourceTask) return
                    const body = buildMainTaskUpdateBodyFromTask(
                        sourceTask,
                        projectId,
                        { start, end }
                    )
                    await dispatch(mainTaskUpdate({ id, body }))
                    refreshTaskList()
                    return
                }
                await updateTaskSupport({
                    _id: id,
                    updateTask: {
                        projectId: projectId,
                        start: start,
                        end: end,
                    },
                })
                refreshTaskList()
            } catch (error) {
                console.log('🚀 ~ updateTaskOnDreg ~ error:', error)
            }
        },
        [projectId, isMainList, apiData, dispatch, refreshTaskList]
    )

    useEffect(() => {
        if (apiRef.current) {
            apiRef.current.on('update-task', (event) => {
                updateTaskOnDreg(event.id, event.task?.start, event.task?.end)
            })
        }
    }, [updateTaskOnDreg])

    useEffect(() => {
        const gridBody = document.querySelector('.sv-gantt__grid-body')
        const timelineBody = document.querySelector('.sv-gantt__timeline-body')

        if (!gridBody || !timelineBody) return

        const onGridScroll = () => {
            timelineBody.scrollTop = gridBody.scrollTop
        }

        const onTimelineScroll = () => {
            gridBody.scrollTop = timelineBody.scrollTop
        }

        gridBody.addEventListener('scroll', onGridScroll)
        timelineBody.addEventListener('scroll', onTimelineScroll)

        return () => {
            gridBody.removeEventListener('scroll', onGridScroll)
            timelineBody.removeEventListener('scroll', onTimelineScroll)
        }
    }, [])

    const scales = [
        { unit: 'month', step: 1, format: '%F %Y' },
        { unit: 'week', step: 1, format: 'Week %W' },
        { unit: 'day', step: 1, format: '%j' },
    ]

    const containerRef = useRef(null)
    const [availableHeight, setAvailableHeight] = useState(0)

    useLayoutEffect(() => {
        if (!containerRef.current) return

        const updateHeight = () => {
            const rect = containerRef.current.getBoundingClientRect()
            setAvailableHeight(window.innerHeight - rect.top)
        }

        updateHeight()
        window.addEventListener('resize', updateHeight)
        return () => window.removeEventListener('resize', updateHeight)
    }, [])

    const ROW_HEIGHT = 36
    const HEADER_HEIGHT = 56

    const contentHeight = HEADER_HEIGHT + tasks.length * ROW_HEIGHT + 80

    const ganttHeight = availableHeight
        ? Math.min(contentHeight, availableHeight)
        : contentHeight

    return (
        <div ref={containerRef} className={styles.roadmapPage}>
            <Willow>
                <div
                    className={styles.ganttSurface}
                    style={{ height: ganttHeight }}
                >
                    <Gantt
                        tasks={tasks}
                        links={links}
                        columns={columns}
                        scales={scales}
                        start={startDate}
                        end={endDate}
                        collapsible
                        ref={apiRef}
                    />
                </div>
            </Willow>
        </div>
    )
}

export default ProjectRoadMap
